import React, { useCallback, useEffect } from 'react';
import NumberFormat from 'react-number-format';

import { getMinCountError, getTextField } from '@lender/utils';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { PropTypes } from 'prop-types';
import { array, object } from 'yup';

import useForm from 'hooks/useForm';

import { LoanHeadersTestIds } from '../../../LoanHeaders.testIds';

// A joint credit file only matches at the bureau when the reissue names every borrower it covers
// (FH-1450), so the borrowers are pre-selected. Borrowers and non-borrower entities cannot ride the same
// association, so the default never mixes them — NBEs are seeded only when there is no borrower to target.
const getInitialFormState = options => {
  const borrowerOptions = options.filter(({ isNbe }) => !isNbe);

  return { options: borrowerOptions.length ? borrowerOptions : options };
};

const getSchema = withOptionSelection =>
  object().shape({
    referenceNumber: getTextField(),
    ...(withOptionSelection && {
      options: array().of(object()).min(1, getMinCountError('borrower')),
    }),
  });

const defaultOptions = [];

const AssociateReportForm = ({
  onSubmitAction,
  isLoading,
  setIsLoading,
  formRef,
  withOptionSelection = false,
  selectLabel = '',
  options = defaultOptions,
}) => {
  const {
    form,
    validationErrors,
    validateForm,
    handleChange,
    isFormChanged,
    setSchema,
    setInForm,
    validateField,
  } = useForm(getInitialFormState(options));

  useEffect(() => {
    const schema = getSchema(withOptionSelection);

    setSchema(schema);
  }, [setSchema, withOptionSelection]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      onSubmitAction(form).finally(() => setIsLoading(false));
    },
    [form, validateForm, onSubmitAction, setIsLoading],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <ModalFormWrapper
      data-testid={LoanHeadersTestIds.ASSOCIATE_REPORT_FORM}
      formRef={formRef}
      isFormChanged={isFormChanged}
      isSubmitting={isLoading}
      submitButtonName="Submit"
      onSubmit={onSubmit}
    >
      {withOptionSelection && (
        <AutocompleteCDS
          multiple
          required
          containerClassName="mb-24"
          dataTestId={LoanHeadersTestIds.ASSOCIATE_REPORT_FORM_OPTION_SELECT}
          error={!!validationErrors.options}
          helperText={validationErrors.options}
          label={selectLabel}
          name="options"
          optionName="name"
          options={options}
          value={form.options}
          onChange={onAutocompleteChange}
        />
      )}
      <NumberFormat
        fullWidth
        required
        allowNegative={false}
        className="mt-8 mb-16"
        customInput={TextFieldCDS}
        decimalSeparator={false}
        disabled={isLoading}
        error={!!validationErrors.referenceNumber}
        helperText={validationErrors.referenceNumber}
        label="Reference Number"
        name="referenceNumber"
        type="text"
        value={form.referenceNumber}
        variant="outlined"
        onChange={handleChange}
      />
    </ModalFormWrapper>
  );
};

AssociateReportForm.propTypes = {
  ...WithLoadingPropTypes,
  withOptionSelection: PropTypes.bool,
  selectLabel: PropTypes.string,
  options: PropTypes.array,
};

export default withLoading(AssociateReportForm);
